import React, { useEffect, useState } from "react";
import { FaShoppingCart, FaBox, FaUsers, FaStore, FaGift, FaBoxes } from "react-icons/fa";
import { MdCurrencyRupee } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { getDashboardMetrics } from "../services/ClientService"; // <-- Updated import

// Helper function to format numbers with commas
const formatCurrency = (amount) => {
  return amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

const Dashboard = () => {
  const [metrics, setMetrics] = useState({
    revenueOnline: 0,
    revenueOffline: 0,
    totalRevenue: 0,
    orders: 0,
    products: 0,
    customers: 0,
  });
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Get user role
  const getUserRole = () => {
    try {
      const userData = JSON.parse(localStorage.getItem("userData"));
      return userData ? userData.role : null;
    } catch (error) {
      return null;
    }
  };

  const getSupportAdminAccess = () => {
    try {
      const userData = JSON.parse(localStorage.getItem("userData"));
      return userData ? userData.supportAdminAccess : false;
    } catch (error) {
      return false;
    }
  };

  const userRole = getUserRole();
  const supportAdminAccess = getSupportAdminAccess();
  const isEmployeeWithoutSupport = userRole === 2 && !supportAdminAccess;

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        // Fetch dashboard metrics from API
        const metricsResponse = await getDashboardMetrics({}); // You can pass filters if needed

        if (metricsResponse?.success && metricsResponse.data) {
          const data = metricsResponse.data;
          
          // Set metrics from API response
          setMetrics({
            revenueOnline: data.totalRevenueOnline || 0,
            revenueOffline: data.totalRevenueOffline || 0,
            totalRevenue: (data.totalRevenueOnline || 0) + (data.totalRevenueOffline || 0),
            orders: data.ordersCount || 0,
            products: data.productsCount || 0,
            customers: data.customersCount || 0,
          });

          // Set recent orders from API response
          const ordersData = (data.recentOrders || [])
            .slice(0, 4)
            .map(order => {
              return {
                id: order.id,
                customer: order.customer,
                amount: order.amount,
                date: order.date,
                status: order.status,
              };
            });
          
          setRecentOrders(ordersData);
        } else {
          // Fallback to empty data if API fails
          setMetrics({
            revenueOnline: 0,
            revenueOffline: 0,
            totalRevenue: 0,
            orders: 0,
            products: 0,
            customers: 0,
          });
          setRecentOrders([]);
        }
      } catch (e) {
        console.error("Error fetching dashboard data:", e);
        setRecentOrders([]);
        setMetrics({
          revenueOnline: 0,
          revenueOffline: 0,
          totalRevenue: 0,
          orders: 0,
          products: 0,
          customers: 0,
        });
      }
      setLoading(false);
    };

    fetchDashboardData();
  }, []);

  const quickActions = [
    { icon: <FaBox />, label: "Add Product", description: "Create a new product listing", link: "/createProduct" },
    { icon: <FaStore />, label: "New Branch", description: "Set up a new store branch", link: "/createBranch" },
    { icon: <FaGift />, label: "Create Coupon", description: "Design a new promotional offers", link: "/createCoupon" },
    { icon: <FaBoxes />, label: "Inventory", description: "Manage your inventory", link: "/inventory" }, // Added Inventory action
  ];

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {loading ? (
        <div className="fixed inset-0 flex justify-center items-center bg-white/80 z-50">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : (
        <>
          {/* Metrics Section */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-6 mb-6">
            <div className="bg-white border-2 border-blue-100 rounded-lg shadow-md p-6 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Online Revenue</h3>
                <p className="text-2xl font-bold">
                  {isEmployeeWithoutSupport ? "*****" : `₹${formatCurrency(metrics.revenueOnline.toFixed(0))}`}
                </p>
                {/* <p className="text-sm text-green-500">+0% from last month</p> */}
              </div>
              <MdCurrencyRupee size="2em" className="text-blue-500" />
            </div>
            <div className="bg-white border-2 border-purple-100 rounded-lg shadow-md p-6 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Offline Revenue</h3>
                <p className="text-2xl font-bold">
                  {isEmployeeWithoutSupport ? "*****" : `₹${formatCurrency(metrics.revenueOffline.toFixed(0))}`}
                </p>
                {/* <p className="text-sm text-green-500">+0% from last month</p> */}
              </div>
              <MdCurrencyRupee size="2em" className="text-purple-500" />
            </div>
            <div 
              className="bg-white border-2 border-green-100 hover:border-green-500 transition-transform duration-300 rounded-lg shadow-md p-6 flex items-center justify-between cursor-pointer"
              onClick={() => navigate("/orders")}
            >
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Orders</h3>
                <p className="text-2xl font-bold">{metrics.orders}</p>
                {/* <p className="text-sm text-green-500">+0% from last month</p> */}
              </div>
              <FaShoppingCart size="2em" className="text-green-500" />
            </div>
            <div 
              className="bg-white border-2 border-yellow-100 hover:border-yellow-500 transition-transform duration-300 rounded-lg shadow-md p-6 flex items-center justify-between cursor-pointer"
              onClick={() => navigate("/products")}
            >
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Products</h3>
                <p className="text-2xl font-bold">{metrics.products}</p>
                {/* <p className="text-sm text-green-500">+0% from last month</p> */}
              </div>
              <FaBox size="2em" className="text-yellow-500" />
            </div>
            <div 
              className="bg-white border-2 border-red-100 hover:border-red-500 transition-transform duration-300 rounded-lg shadow-md p-6 flex items-center justify-between cursor-pointer"
              onClick={() => navigate("/customers")}
            >
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Customers</h3>
                <p className="text-2xl font-bold">{metrics.customers}</p>
                {/* <p className="text-sm text-red-500">-0% from last month</p> */}
              </div>
              <FaUsers size="2em" className="text-red-500" />
            </div>
          </div>

          {/* Recent Orders Section */}
          <div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-3 md:gap-6">
            <div className="col-span-2 bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-xl font-bold mb-4 text-gray-800">Recent Orders</h3>
              <div className="space-y-4">
                {recentOrders.map((order) => (
                  <div
                    key={order.id}
                    className="flex justify-between items-center bg-gray-50 p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => navigate("/orders")}
                  >
                    <div>
                      <p className="font-semibold text-gray-800">{order.id}</p>
                      <p className="text-sm text-gray-600">{order.customer}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-gray-800">₹{formatCurrency(order.amount.toFixed(2))}</p>
                      <p className="text-sm text-gray-600">{order.date}</p>
                    </div>
                    <span
                      className={`px-3 py-1 text-xs font-semibold rounded-full ${
                        order.status === "delivered"
                          ? "bg-green-100 text-green-600"
                          : order.status === "shipped"
                          ? "bg-blue-100 text-blue-600"
                          : "bg-yellow-100 text-yellow-600"
                      }`}
                    >
                      {order.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Quick Actions Section */}
            <div className="p-6 pb-0">
              <h3 className="text-xl font-bold mb-4 text-gray-800">Quick Actions</h3>
              <div
                className={`grid grid-cols-1 sm:grid-cols-2 ${
                  quickActions.length > 4 ? "md:grid-cols-3" : "md:grid-cols-2"
                } gap-6 justify-center`}
              >
                {quickActions.map((action, index) => (
                  <motion.div
                    key={index}
                    onClick={() => navigate(action.link)}
                    className="flex flex-col items-center px-4 py-3 bg-white rounded-lg shadow-lg transition-transform duration-300 cursor-pointer"
                    style={{
                      borderLeft: "12px solid",
                      borderImageSlice: 1,
                      borderImageSource: "linear-gradient(to bottom, #6a11cb, #2575fc)",
                    }}
                    whileHover={{ scale: 1.05}}
                  >
                    <div className="text-blue-600 mb-2.5 text-3xl">{action.icon}</div>
                    <h4 className="font-semibold text-base text-gray-800">{action.label}</h4>
                    <p className="text-xs text-gray-600 text-center">{action.description}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Dashboard;
